import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, Loader2, Book, Download, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BookPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('processing');
  const [book, setBook] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMsg('Missing session.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    let cancelled = false;

    const poll = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const { data } = await axios.get(
          `${API}/api/downloads/purchase-success/by-session/${sessionId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (cancelled) return;
        if (data?.payment_status === 'paid' && data.book) {
          setBook(data.book);
          setStatus('success');
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else if (data?.book) {
          // Stripe says not yet paid but the row exists — show it anyway
          setBook(data.book);
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMsg('Payment not confirmed yet. Refresh in a moment.');
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setErrorMsg(detail || 'Could not load purchase.');
        setStatus('error');
      }
    };

    poll();
    return () => { cancelled = true; };
    // API and axios are stable
  }, [sessionId]);

  const handleDownload = async () => {
    if (!book?.id || downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/downloads/${book.id}/download`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
        }
      );

      const cd = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = `IAMHETHATIS - ${(book.title || 'Book').replace(/[\\/:*?"<>|]+/g, '')}.pdf`;
      if (cd) {
        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
        if (utf8Match) {
          try { filename = decodeURIComponent(utf8Match[1]); } catch (_) { /* keep default */ }
        } else if (asciiMatch) {
          filename = asciiMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: response.data.type || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      alert(`Download failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center pt-24 pb-32" data-testid="book-purchase-success-page">
      <div className="max-w-md w-full mx-auto px-6 text-center">
        <div className="bg-[#1A1A1A] p-12 rounded-lg border border-[#D4AF37]/20">
          {status === 'processing' && (
            <>
              <Loader className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4">Processing Purchase...</h1>
              <p className="text-[#A3A3A3]">Confirming your payment and unlocking access...</p>
            </>
          )}

          {status === 'success' && book && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4 text-green-500">Purchase Complete!</h1>

              <div className="flex flex-col items-center mb-6">
                {book.cover_image_url ? (
                  <img
                    src={book.cover_image_url}
                    alt={book.title}
                    className="w-32 h-44 object-cover rounded mb-4 shadow-lg"
                  />
                ) : (
                  <Book className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                )}
                <p
                  className="text-white font-medium select-text cursor-text"
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                  data-testid="book-success-title"
                >
                  {book.title}
                </p>
                {book.author && (
                  <p className="text-sm text-[#A3A3A3] mt-1">by {book.author}</p>
                )}
              </div>

              <button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="download-book-btn"
              >
                {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                {downloading ? 'Downloading...' : 'Download Your Book (PDF)'}
              </button>

              <p className="text-xs text-[#737373] mt-4">
                Each download is uniquely watermarked to your account.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/downloads"
                  className="flex-1 px-4 py-2 border border-white/20 text-white text-sm uppercase tracking-wider hover:border-[#D4AF37] transition-colors"
                >
                  My Library
                </Link>
                <Link
                  to="/account"
                  className="flex-1 px-4 py-2 border border-white/20 text-white text-sm uppercase tracking-wider hover:border-[#D4AF37] transition-colors"
                >
                  My Account
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4 text-red-400">Purchase Issue</h1>
              <p className="text-[#A3A3A3] mb-6">
                {errorMsg || 'There was an issue processing your purchase. Please try again or contact support.'}
              </p>
              <Link
                to="/downloads"
                className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors inline-block"
              >
                Return to Books
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookPurchaseSuccessPage;
